import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen, Search, Plus, FileText, Download, Calendar, Tag, User,
  ChevronRight, Trash2, Filter, RefreshCw, Cloud, Sparkles, Loader2, Zap,
  Upload, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import api from '../lib/api';

const STATUS_COLORS = {
  published: 'bg-green-100 text-green-700',
  draft: 'bg-yellow-100 text-yellow-700',
  archived: 'bg-gray-100 text-gray-500',
};

const KnowledgeBasePage = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [smartSearch, setSmartSearch] = useState(false);
  const [smartResults, setSmartResults] = useState([]);
  const [smartSearching, setSmartSearching] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({ title: '', category: 'General', content_markdown: '' });
  const [uploadFile, setUploadFile] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchArticles();
  }, [categoryFilter, statusFilter]);

  useEffect(() => {
    if (!smartSearch) {
      const timer = setTimeout(() => fetchArticles(), 300);
      return () => clearTimeout(timer);
    }
  }, [search]);

  const handleSmartSearch = async () => {
    if (!search.trim()) return;
    setSmartSearching(true);
    setSmartResults([]);
    try {
      const res = await api.smartSearchKB(search.trim());
      setSmartResults(res.data.results || []);
      if (res.data.results?.length === 0) {
        toast.info('No matching articles found');
      }
    } catch (err) {
      toast.error('Smart search failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSmartSearching(false);
    }
  };

  const handleSearchKeyDown = (e) => {
    if (smartSearch && e.key === 'Enter') {
      handleSmartSearch();
    }
  };

  const handleUploadArticle = async () => {
    if (!uploadForm.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!uploadForm.content_markdown.trim() && !uploadFile) {
      toast.error('Provide either article content or upload a DOCX file');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('title', uploadForm.title.trim());
      formData.append('category', uploadForm.category);
      if (uploadFile) {
        formData.append('file', uploadFile);
      }
      if (uploadForm.content_markdown.trim()) {
        formData.append('content_markdown', uploadForm.content_markdown.trim());
      }
      const res = await api.uploadKBArticle(formData);
      if (res.data.success) {
        toast.success('KB article uploaded!');
        setShowUploadDialog(false);
        setUploadForm({ title: '', category: 'General', content_markdown: '' });
        setUploadFile(null);
        fetchArticles();
        navigate(`/kb/${res.data.article.kb_id}`);
      }
    } catch (err) {
      toast.error('Upload failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
    }
  };

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (categoryFilter !== 'all') params.category = categoryFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await api.getKBArticles(params);
      setArticles(res.data.articles || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch KB articles:', err);
      toast.error('Failed to load Knowledge Base articles');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (e, kbId) => {
    e.stopPropagation();
    if (!window.confirm('Delete this KB article? This cannot be undone.')) return;
    try {
      await api.deleteKBArticle(kbId);
      toast.success('Article deleted');
      fetchArticles();
    } catch (err) {
      toast.error('Failed to delete article');
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  return (
    <div className="space-y-6" data-testid="knowledge-base-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-amber-50">
            <BookOpen className="h-6 w-6 text-amber-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800">Knowledge Base</h1>
            <p className="text-sm text-slate-500">{total} article{total !== 1 ? 's' : ''}</p>
          </div>
        </div>
        <Button
          onClick={() => setShowUploadDialog(true)}
          className="gap-2 bg-amber-600 hover:bg-amber-700"
          data-testid="kb-upload-article-btn"
        >
          <Upload className="h-4 w-4" />
          Upload Article
        </Button>
      </div>

      {/* Upload Dialog */}
      {showUploadDialog && (
        <Card className="border-amber-200 bg-amber-50/30" data-testid="kb-upload-dialog">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <Upload className="h-4 w-4 text-amber-600" />
                Upload Existing KB Article
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowUploadDialog(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Title *</label>
                <Input
                  placeholder="e.g., External SharePoint User Receives AADSTS90072"
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, title: e.target.value }))}
                  data-testid="kb-upload-title"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Category</label>
                <Select value={uploadForm.category} onValueChange={(v) => setUploadForm(prev => ({ ...prev, category: v }))}>
                  <SelectTrigger data-testid="kb-upload-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="General">General</SelectItem>
                    <SelectItem value="Email">Email</SelectItem>
                    <SelectItem value="Network">Network</SelectItem>
                    <SelectItem value="Hardware">Hardware</SelectItem>
                    <SelectItem value="Software">Software</SelectItem>
                    <SelectItem value="Access/Permissions">Access/Permissions</SelectItem>
                    <SelectItem value="Phone/Voicemail">Phone/Voicemail</SelectItem>
                    <SelectItem value="Microsoft 365">Microsoft 365</SelectItem>
                    <SelectItem value="Security">Security</SelectItem>
                    <SelectItem value="SharePoint">SharePoint</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="text-xs text-gray-500 mb-1 block">Upload DOCX File (optional — text will be extracted)</label>
              <div className="flex items-center gap-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".docx"
                  className="hidden"
                  onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                  data-testid="kb-upload-file-input"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  className="gap-1.5"
                  data-testid="kb-upload-file-btn"
                >
                  <FileText className="h-4 w-4" />
                  {uploadFile ? uploadFile.name : 'Choose File'}
                </Button>
                {uploadFile && (
                  <Button variant="ghost" size="sm" onClick={() => { setUploadFile(null); if(fileInputRef.current) fileInputRef.current.value = ''; }}>
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </div>

            {!uploadFile && (
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Or paste article content (Markdown)</label>
                <Textarea
                  placeholder="Paste your KB article content here in Markdown format..."
                  value={uploadForm.content_markdown}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, content_markdown: e.target.value }))}
                  rows={8}
                  className="font-mono text-sm"
                  data-testid="kb-upload-content"
                />
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowUploadDialog(false)}>Cancel</Button>
              <Button
                onClick={handleUploadArticle}
                disabled={uploading}
                className="gap-2 bg-amber-600 hover:bg-amber-700"
                data-testid="kb-upload-submit"
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {uploading ? 'Uploading...' : 'Upload Article'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          {smartSearch ? (
            <Sparkles className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-purple-500" />
          ) : (
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          )}
          <Input
            className={`pl-9 ${smartSearch ? 'border-purple-200 focus:border-purple-400' : ''}`}
            placeholder={smartSearch ? "Ask a question, e.g. 'guest user can\\'t access SharePoint'..." : "Search articles by title, ID, or content..."}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            data-testid="kb-search-input"
          />
        </div>
        <Button
          variant={smartSearch ? "default" : "outline"}
          size="sm"
          onClick={() => {
            setSmartSearch(!smartSearch);
            setSmartResults([]);
          }}
          className={smartSearch ? "gap-1.5 bg-purple-600 hover:bg-purple-700" : "gap-1.5"}
          data-testid="kb-smart-search-toggle"
          title="AI-powered semantic search"
        >
          <Zap className="h-4 w-4" />
          <span className="hidden md:inline">AI Search</span>
        </Button>
        {smartSearch && (
          <Button
            size="sm"
            onClick={handleSmartSearch}
            disabled={smartSearching || !search.trim()}
            className="gap-1.5 bg-purple-600 hover:bg-purple-700"
            data-testid="kb-smart-search-btn"
          >
            {smartSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            Search
          </Button>
        )}
        {!smartSearch && (
          <>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-40" data-testid="kb-category-filter">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="Email">Email</SelectItem>
                <SelectItem value="Network">Network</SelectItem>
                <SelectItem value="Hardware">Hardware</SelectItem>
                <SelectItem value="Software">Software</SelectItem>
                <SelectItem value="Access">Access/Permissions</SelectItem>
                <SelectItem value="Phone">Phone/Voicemail</SelectItem>
                <SelectItem value="Microsoft 365">Microsoft 365</SelectItem>
                <SelectItem value="Security">Security</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36" data-testid="kb-status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="published">Published</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </>
        )}
        <Button variant="outline" size="sm" onClick={fetchArticles} data-testid="kb-refresh-btn">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {/* Smart Search Results */}
      {smartSearch && smartResults.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-purple-600 font-medium flex items-center gap-1.5">
            <Sparkles className="h-3 w-3" />
            AI found {smartResults.length} relevant article{smartResults.length !== 1 ? 's' : ''}
          </p>
          {smartResults.map((result) => (
            <Card
              key={result.kb_id}
              className="cursor-pointer hover:border-purple-300 hover:shadow-md transition-all border-purple-100"
              onClick={() => navigate(`/kb/${result.kb_id}`)}
              data-testid={`kb-smart-result-${result.kb_id}`}
            >
              <CardContent className="py-4 px-5">
                <div className="flex items-center gap-4">
                  <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold
                    ${result.relevance >= 70 ? 'bg-green-100 text-green-700' :
                      result.relevance >= 40 ? 'bg-amber-100 text-amber-700' :
                      'bg-slate-100 text-slate-600'}`}
                  >
                    {result.relevance}%
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-mono text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">{result.doc_id}</span>
                      {result.category && (
                        <Badge variant="outline" className="text-xs">{result.category}</Badge>
                      )}
                    </div>
                    <h3 className="font-medium text-slate-800">{result.title}</h3>
                    {result.reason && (
                      <p className="text-xs text-purple-500 mt-1 flex items-center gap-1">
                        <Sparkles className="h-3 w-3" />
                        {result.reason}
                      </p>
                    )}
                  </div>
                  <ChevronRight className="h-5 w-5 text-slate-300 flex-shrink-0" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Articles List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin h-8 w-8 border-4 border-amber-500 border-t-transparent rounded-full" />
        </div>
      ) : articles.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <BookOpen className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 font-medium">No KB articles yet</p>
            <p className="text-sm text-slate-400 mt-1">
              Generate articles from resolved tickets using the "Generate KB Article" button on any ticket.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {articles.map((article) => (
            <Card
              key={article.kb_id}
              className="cursor-pointer hover:border-amber-300 hover:shadow-md transition-all"
              onClick={() => navigate(`/kb/${article.kb_id}`)}
              data-testid={`kb-article-${article.kb_id}`}
            >
              <CardContent className="py-4 px-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 min-w-0 flex-1">
                    <div className="flex-shrink-0 p-2 rounded bg-amber-50">
                      <FileText className="h-5 w-5 text-amber-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-mono text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">
                          {article.doc_id}
                        </span>
                        <Badge className={`text-xs ${STATUS_COLORS[article.status] || 'bg-gray-100'}`}>
                          {article.status}
                        </Badge>
                        {article.category && (
                          <Badge variant="outline" className="text-xs">
                            {article.category}
                          </Badge>
                        )}
                        {article.sharepoint_url && (
                          <Badge className="text-xs bg-blue-50 text-blue-600 gap-1">
                            <Cloud className="h-2.5 w-2.5" />
                            SharePoint
                          </Badge>
                        )}
                      </div>
                      <h3 className="font-medium text-slate-800 truncate">{article.title}</h3>
                      <div className="flex items-center gap-4 mt-1 text-xs text-slate-400">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(article.created_at)}
                        </span>
                        {article.source_ticket_number && (
                          <span className="flex items-center gap-1">
                            <Tag className="h-3 w-3" />
                            Ticket #{article.source_ticket_number}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {article.created_by || 'AI Generated'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-400 hover:text-red-600"
                      onClick={(e) => handleDelete(e, article.kb_id)}
                      data-testid={`kb-delete-${article.kb_id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    <ChevronRight className="h-5 w-5 text-slate-300" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default KnowledgeBasePage;
